import { BugOutlined } from '@ant-design/icons';
import { Badge } from 'antd';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { RelativeTime } from 'components/mx-data-display/RelativeTime';
import { Tag } from 'components/tag/Tag';

import { useMarkNotificationAsRead } from 'hooks/inbox-notifications/useInboxNotifications';

import { featureFlags } from 'utils/config/featureFlags';

import { useAuthStore } from 'store/auth.store';

import { useInboxNotificationContext } from 'layout/inbox-notifications/InboxNotificationContext';
import { unreadNotificationsColor } from 'layout/inbox-notifications/_notificationUtils';

import type { InboxBaseNotification } from 'types/inbox-notifications/InboxNotifications';

const { FF__SHOW_NOTIFICATIONS_DEBUG_MODE: DEBUG_MODE } = featureFlags;

interface GenericNotificationItemProps {
  item: InboxBaseNotification;
  url: string;
  avatar: ReactNode;
  title: ReactNode;
  description: ReactNode;
}

export function GenericNotificationItem({
  item,
  url,
  avatar,
  title,
  description,
}: GenericNotificationItemProps) {
  const markAsReadMutation = useMarkNotificationAsRead();
  const { onClose } = useInboxNotificationContext();
  const { user } = useAuthStore();

  const handleClick = () => {
    if (!item.is_read && item.id) {
      markAsReadMutation.mutate(item.id);
    }
    onClose();
  };

  return (
    <Link
      to={url}
      className="block hover:bg-primary/7 transition-colors rounded-md p-2"
      onClick={handleClick}
    >
      <div className="flex gap-2">
        <div className="shrink-0">{avatar}</div>
        <div className="flex flex-col gap-1 grow">
          <div className="flex items-start justify-between gap-1">
            <span className="font-medium">{title}</span>
            <span className="flex items-center gap-1">
              <span className="text-xs text-color-text-secondary">
                <RelativeTime date={item.created_at} shorthand />
              </span>
              {!item.is_read && <Badge dot color={unreadNotificationsColor} />}
            </span>
          </div>
          <div className="text-muted">{description}</div>
          {DEBUG_MODE && user?.id && item.debug_user_id !== user?.id && (
            <Tag
              variant="outlined"
              icon={<BugOutlined />}
              title={item.debug_changelog_id}
              className="text-mauve-grey-8 text-xs font-mono py-1 mt-1"
            >
              [DEBUG] Receiver: {item.debug_user_name ?? item.debug_user_id}
            </Tag>
          )}
        </div>
      </div>
    </Link>
  );
}
